import { ref } from 'vue'
import { useStoreCompat } from '@/composables/useStoreCompat'
import { useRest } from '@/composables/useRest'
import { useToast } from '@/composables/useToast'

export function useDialog({
    causaJuzgado,
    causaCaratula,
    crimes,
    listaCaratulas,
    articulos,
    nroActuacion,
    ufiInterviniente,
    assistants,
    prosecutor,
    sumario,
    displayModal,
    selectedDropdownValue,
    delitoList,
    resumen,
    causasList,
    showMo,
    articulosList,
    juzgadosList,
    numeroUfiList,
    ayudanteFiscal,
    fiscal,
    refs,
}) {
    const modalAttributes = ref({})
    const confirmationMode = ref(false)
    const modalSpinner = ref(false)
    const allowEmptySave = ref(true)
    const blankArray = ref([])
    const needConfirm = ref(false)
    
    const hasDeletedItem= ref(false)
    const store = useStoreCompat()
    const rest = useRest()
    const toast = useToast()

    const saveModalData = async () => {
        if(needConfirm.value){
            loadConfirmModalAttributes()
            return
        }
        modalSpinner.value = true
        let respuesta = null

        if ((modalAttributes.value.type === 'Juzgado interviniente' || modalAttributes.value.type === 'Interviniente') && causaJuzgado.value !== '') {
            respuesta = await store.dispatch('sumario/updateJuzgado', { JuzgadoID: causaJuzgado.value.JuzgadoID, Nombre: causaJuzgado.value.Nombre, VueCli: this })
            if (respuesta != null) {
                await updateSumario(respuesta)
                toastSuccess()
            }
        }

        if ((modalAttributes.value.type === 'Causa/Carátula') && causaCaratula.value !== '') {
            respuesta = await store.dispatch('sumario/updateSumario', { causaCaratula: causaCaratula.value, VueCli: this });
            if (respuesta != null) {
                await updateSumario(respuesta)
                toastSuccess()
            }
        }

        if (modalAttributes.value.type === 'Delito') {
            if (crimes.value?.length == 0) {
                toast.error("ERROR: Se debe definir al menos un delito")
            } else {
                //Actualiza el store con los nuevos datos, antes de enviarlos
                await store.dispatch('sumario/setValue', { field: 'listaCaratulas', value: crimes.value });
                try {
                    const resp = await rest.put({
                        url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/sumario/${sumario.value?.id}`,
                        formData: sumario.value,
                        params: { method: 'own', section: 'Delito' }
                    });
                }
                catch (e) {
                    toast.error("ERROR: Al actualizar los delitos")
                }
            }
        }

        if (modalAttributes.value.type === 'Causas/Carátulas') {
            if (listaCaratulas.value?.length == 0) {
                toast.error("ERROR: Se debe definir al menos una Causa")
            } else {
                await store.dispatch('sumario/setValue', { field: 'listaCaratulas', value: listaCaratulas.value });
                try {
                    const resp = await rest.put({
                        url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/sumario/${sumario.value?.id}`,
                        formData: sumario.value,
                        params: { method: 'own', section: 'listaCaratulas' }
                    });
                    if(resp != null){
                        toastSuccess()
                    }
                } catch (e) {
                    console.log('Error: ', e);
                    toast.error("ERROR: Al actualizar las causas")
                }
            }
        }

        if (modalAttributes.value.type === 'Carátulas del Expediente' && articulos.value.length !== 0) {
            respuesta = await store.dispatch('sumario/updateArticulos', { articulos: articulos.value, VueCli: this });
            if (respuesta != null) {
                await updateSumario(respuesta)
                toastSuccess()
            }
        }

        if (modalAttributes.value.type === 'N° de Actuación') {
            if (nroActuacion.value === '') {
                toast.error("ERROR: N° de Actuación invalido")
            } else {
                respuesta = await store.dispatch('sumario/updateSumario', { Numero: nroActuacion.value, VueCli: this });
                if (respuesta != null) {
                    await updateSumario(respuesta)
                    toastSuccess()
                }
            }
        }

        if (modalAttributes.value.type === 'Ufi Interviniente' && nroActuacion.value !== '') {
            respuesta = await store.dispatch('sumario/updateUfi', { tipoUfi: ufiInterviniente.value.TipoufiID, VueCli: this });
            if (respuesta != null) {
                await updateSumario(respuesta)
                toastSuccess()
            }
        }

        if (modalAttributes.value.type === "Ayudante Fiscal") {
            await store.dispatch('sumario/setValue', { field: 'assistant', value: assistants.value });

            await rest.put({
                url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/sumario/${sumario.value?.id}`,
                formData: sumario.value,
                params: { method: 'own', section: 'ayudanteFiscal' }
            });
        }

        if (modalAttributes.value.type === "Fiscal") {
            await store.dispatch('sumario/setValue', { field: 'prosecutor', value: prosecutor.value });

            await rest.put({
                url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/sumario/${sumario.value?.id}`,
                formData: sumario.value,
                params: { method: 'own', section: 'fiscal' }
            });
        }

        causaJuzgado.value = ''
        causaCaratula.value = ''
        nroActuacion.value = sumario.value?.causaNumero
        articulos.value = []
        displayModal.value = false;
        ufiInterviniente.value = ''
        modalSpinner.value = false
        confirmationMode.value = true
        selectedDropdownValue.value = null
        hasDeletedItem.value = false
    }

    const isSaveDisabled = () => {

        if(hasDeletedItem.value){
            return false
        }

        if(modalAttributes.value.type === 'Delito'){
            return crimes.value?.length == 0 ? true : false
        }
        if(modalAttributes.value.type === 'Carátulas del Expediente'){
            return articulos.value?.length == 0 ? true : false
        }
        if (modalAttributes.value.allowEmptySave) {
            return false; // Not disabled if it's allowed to save empty
        }
        // Disable the button if the dropdown value is not selected.
        return !selectedDropdownValue.value;
    }

    const openModal = (type) => {
        listaCaratulas.value = formatCausasCaratulas();
        articulos.value = formatArticulosExpediente()
        crimes.value = sumario.value?.listaCaratulas?.length > 0 ? sumario.value?.listaCaratulas : [delitoList.value.find(e => e.Nombre === resumen.value.caratula)];
        loadModalAttributes(type)
        nroActuacion.value = sumario.value?.causaNumero
        displayModal.value = true;
    }

    const formatCausasCaratulas = () => {
        let newListaCaratulas = [];
        if (sumario.value?.listaCaratulas !== undefined) {
            sumario.value?.listaCaratulas.map((causaCaratula) => {
                newListaCaratulas.push(causasList.value?.filter(item => item.TipocausacaratulaID === causaCaratula.TipocausacaratulaID)[0]);
            });
            return newListaCaratulas;
        }
        return undefined;
    }

    const formatArticulosExpediente = () => {
    let articulosActuales = []
        if (sumario.value?.listaArticulos?.length > 0 && articulosList.value?.length > 0) {
            articulosActuales = articulosList.value.filter(art => 
                sumario.value.listaArticulos.some(a => a.ArticulosID === art.ArticulosID)
            );
        }
        return articulosActuales
    }

    const formatArticulos = () => {
        let newListaArticulos = [];
        if (sumario.value?.listaArticulos !== undefined) {
            sumario.value.listaArticulos.map((articulos) => {
                newListaArticulos.push(articulos.Numeroarticulo)
            });
            return newListaArticulos;
        }
        return undefined;
    }

    const closeModal = () => {
        confirmationMode.value = true
        causaJuzgado.value = ''
        causaCaratula.value = ''
        nroActuacion.value = sumario.value.causaNumero
        articulos.value = []
        displayModal.value = false
    }

    const loadConfirmModalAttributes = () => {
        if (modalAttributes.value.type === "N° de Actuación") {
            confirmationMode.value = true;
            modalAttributes.value.message = 'Esta acción puede afectar el seguimiento de la actuación.'
            needConfirm.value = false
            modalAttributes.value.title = '¿Está seguro de que desea cambiar el N° de legajo/ N° de extracto?'
        }
        if (modalAttributes.value.type === "Juzgado interviniente" || modalAttributes.value.type === "Interviniente") {
            confirmationMode.value = true;
            modalAttributes.value.message = 'Esta acción modificará la información asociada a la actuación.';
            needConfirm.value = false
            modalAttributes.value.title = '¿Está seguro de que desea editar el juzgado interviniente?'
        }
    }

    const loadModalAttributes = (type) => {
        //check actuation type
        let simpleCausaCaratula = resumen.value.caratula && !evaluateIfFormTypeIsUFI() ? true : false;
        let multipleCausaCaratula = resumen.value.listaCaratulas ? true : false;
        let delito = evaluateIfFormTypeIsUFI();
        let expediente = resumen.value.listaArticulos ? true : false;
        //it's multipleCausaCaratula discard option
        if (!simpleCausaCaratula && !delito && !expediente)
            multipleCausaCaratula = true;
        //generic attributes
        confirmationMode.value = false;
        modalAttributes.value.form = "Dropdown";
        modalAttributes.value.divTestId = "b3uttonArt";
        modalAttributes.value.arrayItems = {
            //the default 'blankArray' prevents a warning
            arrayName: 'blankArray',
        };
        if (type === "Caratula") {
            modalAttributes.value.title = 'Editar causa / carátula'
            if (simpleCausaCaratula) {
                modalAttributes.value.type = "Causa/Carátula";
                modalAttributes.value.new = "nueva";
                modalAttributes.value.vModel = "causaCaratula";
                modalAttributes.value.optionsList = causasList.value;
                modalAttributes.value.message = 'Seleccione una o más causas en el listado para actualizar la carátula de la actuación. Asegúrese de que la selección refleje correctamente la naturaleza del hecho.';
            }
            if (multipleCausaCaratula) {
                modalAttributes.value.type = "Causas/Carátulas";
                modalAttributes.value.new = "nuevas";
                modalAttributes.value.vModel = "dataCaratulas";
                modalAttributes.value.optionsList = causasList.value;
                modalAttributes.value.arrayItems = {
                    arrayName: 'listaCaratulas',
                    id: 'TipocausacaratulaID',
                    attribute1: { text: '', prop: 'Nombre' },
                    attribute2: { text: '', prop: '' },
                };
                modalAttributes.value.message = 'Seleccione una o más causas en el listado para actualizar la carátula de la actuación. Asegúrese de que la selección refleje correctamente la naturaleza del hecho.';
            }
            if (delito) {
                modalAttributes.value.title = 'Editar Delitos'
                modalAttributes.value.type = "Delito";
                modalAttributes.value.new = "nuevos";
                modalAttributes.value.optionsList = delitoList.value;
                modalAttributes.value.vModel = "dataCrimes";
                modalAttributes.value.allowEmptySave = false;
                modalAttributes.value.arrayItems = {
                    arrayName: 'crimes',
                    id: 'TipocausacaratulaID',
                    attribute1: { text: '', prop: 'Nombre' },
                    attribute2: { text: '', prop: '' },
                };
                modalAttributes.value.message = 'Seleccione uno o más delitos en el listado para actualizar la carátula de la actuación. Asegúrese de que la selección refleje correctamente la naturaleza del hecho.'
                needConfirm.value = false
            }
            //if it's Causa/Carátula, Causas/Carátulas or Delito
            modalAttributes.value.testId = "dropdownCausaCaratula";
            modalAttributes.value.optionLabel = "Nombre";
            modalAttributes.value.showMo = showMo();
            modalAttributes.value.dataKey = "Nombre";
            if (expediente) {
                modalAttributes.value.title = 'Editar Carátula del expediente'
                modalAttributes.value.type = "Carátulas del Expediente";
                modalAttributes.value.new = "nuevas";
                modalAttributes.value.optionsList = articulosList.value;
                modalAttributes.value.showMo = !showMo()
                modalAttributes.value.dataKey = "Numeroarticulo";
                modalAttributes.value.optionLabel = "Numeroarticulo";
                modalAttributes.value.vModel = "dataArticulos";
                modalAttributes.value.testId = "dropdownArticulos";
                modalAttributes.value.arrayItems = {
                    arrayName: 'articulos',
                    id: 'ArticulosID',
                    attribute1: { text: 'Art   ', prop: 'Numeroarticulo' },
                    attribute2: { text: ' - ', prop: 'Codigo' },
                };
                modalAttributes.value.message = 'Seleccione una o más carátulas del expediente en el listado para actualizar la carátula de la actuación. Asegúrese de que la selección refleje correctamente la naturaleza del hecho.'
                needConfirm.value = false
            }
        } else {
            modalAttributes.value.type = type;
            modalAttributes.value.showMo = true;
            if (modalAttributes.value.type === "Juzgado interviniente" || modalAttributes.value.type === "Interviniente") {
                modalAttributes.value.vModel = "causaJuzgado";
                modalAttributes.value.optionsList = juzgadosList.value;
                modalAttributes.value.optionLabel = "Nombre";
                modalAttributes.value.testId = "dropdownJuzgado";
                modalAttributes.value.divTestId = "ButtonInterviniente";
                modalAttributes.value.allowEmptySave = false;
                modalAttributes.value.message = 'Por favor, seleccione un juzgado interviniente para actualizar esta información. Asegúrese de que la selección sea correcta antes de guardar los cambios.';
                needConfirm.value = true
                modalAttributes.value.title = 'Editar juzgado interviniente'
            }
            if (modalAttributes.value.type === "Ufi Interviniente") {
                modalAttributes.value.vModel = 'ufiInterviniente';
                modalAttributes.value.optionsList = numeroUfiList.value;
                modalAttributes.value.optionLabel = "Numero";
                modalAttributes.value.testId = "dropdownNumeroUfi";
                modalAttributes.value.divTestId = "ButtonInterviniente";
                modalAttributes.value.allowEmptySave = false;
                modalAttributes.value.message = 'Por favor, seleccione un UFI interviniente para actualizar esta información. Asegúrese de que la selección sea correcta antes de guardar los cambios.';
                modalAttributes.value.title = 'Editar UFI interviniente'
            }
            if (modalAttributes.value.type === "N° de Actuación") {
                confirmationMode.value = false;
                modalAttributes.value.form = "InputText";
                modalAttributes.value.vModel = 'nroActuacion';
                modalAttributes.value.divTestId = "ButtonInterviniente";
                modalAttributes.value.allowEmptySave = true;
                modalAttributes.value.message = 'Modifique el número de legajo o extracto con precaución. Asegúrese de que el nuevo valor sea correcto antes de guardar los cambios.'
                needConfirm.value = true
                modalAttributes.value.title = 'Editar número de legajo/extracto'

            }
            if (modalAttributes.value.type === "Ayudante Fiscal") {
                modalAttributes.value.vModel = 'assistants';
                modalAttributes.value.optionsList = ayudanteFiscal.value;
                modalAttributes.value.optionLabel = "Denominacion";
                modalAttributes.value.testId = "dropdownAyudanteFiscal";
                modalAttributes.value.divTestId = "ButtonAyudanteFiscal";
                modalAttributes.value.allowEmptySave = false;
                modalAttributes.value.title = 'Editar Ayudante Fiscal'
                modalAttributes.value.message = ''
            }
            if (modalAttributes.value.type === "Fiscal") {
                modalAttributes.value.vModel = 'prosecutor';
                modalAttributes.value.optionsList = fiscal.value;
                modalAttributes.value.optionLabel = "Denominacion";
                modalAttributes.value.testId = "dropdownFiscal";
                modalAttributes.value.divTestId = "ButtonFiscal";
                modalAttributes.value.allowEmptySave = false;
                modalAttributes.value.message = ''
                modalAttributes.value.title = 'Editar Fiscal'
            }
        }
    }

    const evaluateIfFormTypeIsUFI = () => {
        let isUFI = false;
        isUFI = resumen.value.formType.includes('Unidades Fiscales de Investigacion') ||
            resumen.value.formType.includes('Unidades Fiscales de Investigación') ? true : false;
        return isUFI;
    }

    const deleteModalItem = (itemID) => {
        const items = modalAttributes.value.arrayItems;
        if (!items) return;

        const arrayRef = refs[items.arrayName];
        if (!arrayRef?.value) return;

        arrayRef.value = arrayRef.value.filter(
            (item) => item[items.id] !== itemID
        );

        hasDeletedItem.value = true;
        
        const dialogEl = refs.modalUpdateRef?.value?.$el?.getElementsByClassName?.('p-dialog-content')?.[0];
        if (dialogEl && dialogEl.offsetHeight <= 362) {
            dialogEl.style.overflow = 'visible';
        }
    }

    const changeModal = (params) => {
        const [e, vModel, refName] = params;
        const targetRef = refs[vModel];  
        if (refName === 'ModalDropdown' && targetRef) {
            targetRef.value = e.value;
        }
    
        if (refName === 'ModalInputText' && targetRef) {
            targetRef.value = e.target.value;
        }
    };

    const updateSumario = async (id) => {
        await store.dispatch("sumario/setSumario", id);
    }

    const toastSuccess = () => {
        toast.success("Datos guardados");
    }

    const getIcon = () => {
        return confirmationMode.value ? 'pi pi-question-circle' : 'pi pi-exclamation-triangle'
    }
    const getIconColor = () => {
        return confirmationMode.value ? '#D32F2F' : '#FBC02D'
    }

    return {
        modalAttributes,
        confirmationMode,
        modalSpinner,
        allowEmptySave,
        blankArray,
        saveModalData,
        isSaveDisabled,
        openModal,
        closeModal,
        formatCausasCaratulas,
        loadModalAttributes,
        evaluateIfFormTypeIsUFI,
        formatArticulos,
        changeModal,
        deleteModalItem,
        updateSumario,
        toastSuccess,
        getIcon,
        getIconColor,
    }
}
